/**
 * Définit les limites pour chaque type d'abonnement
 */
export const SUBSCRIPTION_PLANS = {
  basic: {
    name: 'Basique',
    monthlyMissions: 5,
    price: '29.99',
    features: [
      '5 missions par mois',
      'Support prioritaire',
      'Accès aux missions de base'
    ]
  },
  standard: {
    name: 'Standard',
    monthlyMissions: 20,
    price: '59.99',
    features: [
      '20 missions par mois',
      'Support prioritaire',
      'Statistiques avancées',
      'Mise en avant dans les recherches'
    ]
  },
  premium: {
    name: 'Premium',
    monthlyMissions: 'illimité',
    price: '99.99',
    features: [
      'Missions illimitées',
      'Support 24/7',
      'Statistiques avancées',
      'Mise en avant prioritaire',
      'Profil vérifié'
    ]
  }
};

/**
 * Vérifie si un déménageur peut accepter plus de missions ce mois-ci
 * @param {Object} subscription - L'objet d'abonnement du déménageur
 * @param {number} currentMonthMissions - Nombre de missions déjà effectuées ce mois-ci
 * @param {Object} user - Objet utilisateur contenant les informations de période d'essai
 * @returns {Object} { canAccept: boolean, reason: string, upgradeRequired: boolean }
 */
/**
 * Vérifie si un déménageur peut accepter plus de missions ce mois-ci
 * @param {Object} subscription - L'objet d'abonnement du déménageur
 * @param {number} currentMonthMissions - Nombre de missions déjà effectuées ce mois-ci
 * @param {Object} user - Objet utilisateur contenant les informations de période d'essai
 * @returns {Object} { canAccept: boolean, reason: string, upgradeRequired: boolean }
 */
export const canAcceptMoreMissions = (subscription, currentMonthMissions, user = {}) => {
  // Vérifier d'abord si l'utilisateur a un abonnement actif
  if (subscription?.status === 'active' || subscription?.status === 'trialing') {
    // Vérifier si c'est un abonnement premium (illimité)
    if (subscription.subscription_type === 'premium') {
      return {
        canAccept: true,
        reason: 'Abonnement Premium illimité',
        upgradeRequired: false
      };
    }

    // Pour les autres types d'abonnements, vérifier les limites
    const plan = SUBSCRIPTION_PLANS[subscription.subscription_type];
    
    if (!plan) {
      return {
        canAccept: false,
        reason: 'Type d\'abonnement non reconnu',
        upgradeRequired: true
      };
    }

    // Vérifier si la limite mensuelle est atteinte
    if (typeof plan.monthlyMissions === 'number' && currentMonthMissions >= plan.monthlyMissions) {
      return {
        canAccept: false,
        reason: `Vous avez atteint la limite de ${plan.monthlyMissions} missions ce mois-ci`,
        upgradeRequired: true,
        limitReached: true
      };
    }

    // Calculer le nombre de missions restantes
    const remaining = typeof plan.monthlyMissions === 'number' 
      ? plan.monthlyMissions - currentMonthMissions 
      : 'illimité';
      
    return {
      canAccept: true,
      reason: typeof remaining === 'number' 
        ? `Il vous reste ${remaining} mission(s) ce mois-ci` 
        : 'Missions illimitées disponibles',
      upgradeRequired: false
    };
  }

  // Si pas d'abonnement actif, vérifier la période d'essai
  if (user?.is_demenageur && user?.trial_end_date) {
    if (new Date(user.trial_end_date) > new Date()) {
      const daysLeft = Math.ceil((new Date(user.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24));
      return {
        canAccept: true,
        reason: `Période d'essai gratuite - ${daysLeft} jour(s) restant(s)`,
        upgradeRequired: false,
        isTrial: true
      };
    } else {
      return {
        canAccept: false,
        reason: 'Votre période d\'essai est terminée',
        upgradeRequired: true,
        trialEnded: true
      };
    }
  }

  // Si on arrive ici, c'est qu'il n'y a pas d'abonnement actif ni de période d'essai valide
  return {
    canAccept: false,
    reason: 'Aucun abonnement actif',
    upgradeRequired: true
  };
};

/**
 * Récupère les détails d'un plan d'abonnement
 * @param {string} planId - L'identifiant du plan (basic, standard, premium)
 */
export const getPlanDetails = (planId) => {
  return SUBSCRIPTION_PLANS[planId] || null;
};
